import { useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { Box } from '@mui/material';
import { ActiveBudget } from './widgets/activeBudget';
import { BenefitData, BenefitsWidget } from './widgets/benefitsWidget';
import {
  EmployeeBenefitBudget,
  EmployeeBenefitSettings,
  IconEnum
} from '@pxtra-client-lib-legacy/gen';
import Util from '../../../misc/util';
import { format } from 'date-fns';
import { useDate } from '../partials/profilePagesPartial';
import type { Benefit } from '@pxtra-client-lib-legacy/types';
import { BenefitSetWidget } from './widgets/benefitSetWidget';
import { useGetEmployee } from '../../../queries/employee';
import { Loading } from '../../../lib/components';
import { useGetNextMonthBenefitSet, useListBenefitSet } from '../../../queries/benefit/benefitSet';
import { usePaymentData } from '../../../queries/company';
import { checkHasCompanyBenefitSets } from '../../../lib/util/pricingModel';

export const EmployeeProfilDashboard = observer(() => {
  const { employeeStore } = useStore();
  const { employeeId } = useParams();
  const { companyStore } = useStore();
  const { data: employeeData, isLoading: isEmployeeLoading } = useGetEmployee(
    employeeStore.currentEmployee?.id ?? ''
  );
  const { data: paymentData } = usePaymentData(companyStore.company?.id);
  const benefitSetsEnabled = checkHasCompanyBenefitSets(
    paymentData?.pricing_model,
    paymentData?.booked_benefits
  );

  const { data: benefitSetData } = useListBenefitSet(
    companyStore.company?.id ?? '',
    paymentData?.pricing_model,
    paymentData?.booked_benefits
  );
  const companyBenefitSetsExists = benefitSetData?.results && benefitSetData.results.length > 0;

  const [activeBenefits, setActiveBenefits] = useState<BenefitData[]>(new Array<BenefitData>());
  const [desiredBenefits, setDesiredBenefits] = useState<BenefitData[]>(new Array<BenefitData>());

  const [currentBenefitBudget, setCurrentBenefitBudget] = useState<EmployeeBenefitBudget>();

  const [date] = useDate();
  const { data: nextMonthBenefitSet, isLoading: isBenefitSetLoading } = useGetNextMonthBenefitSet(
    employeeStore.currentEmployee?.id ?? '',
    date.getMonth(),
    date.getFullYear(),
    paymentData?.pricing_model,
    paymentData?.booked_benefits
  );

  const getBenefitData = (predicate: (benefit: Benefit) => boolean): BenefitData[] => {
    const benefitSettings = employeeStore.currentEmployee?.benefit_settings;
    if (!benefitSettings) throw Error('benefit_settings missing!');

    const benefitKeys: (keyof EmployeeBenefitSettings | 'custom')[] =
      Util.getKeys<EmployeeBenefitSettings>(benefitSettings);
    benefitKeys.push('custom');
    const benefits = new Array<BenefitData>();

    benefitKeys.forEach((key) => {
      if (predicate(key as Benefit)) {
        if (key == 'custom') {
          const budget = currentBenefitBudget?.custom;
          budget?.forEach((customBudget) => {
            benefits.push({
              key: key,
              usedBudget: customBudget.amount,
              label: customBudget.name,
              icon: customBudget.icon as IconEnum
            });
          });
        } else {
          const budget = currentBenefitBudget?.[key as keyof EmployeeBenefitBudget] as number;
          benefits.push({
            key: key,
            usedBudget: budget ?? 0
          });
        }
      }
    });

    return benefits.sort((a, b) => b.usedBudget - a.usedBudget);
  };

  const isBenefitActiveForPeriod = (benefit: Benefit | 'custom') => {
    if (benefit === 'custom') return true;
    return currentBenefitBudget?.active_benefits?.includes(benefit) ?? false;
  };

  const getActiveBenefits = () => setActiveBenefits(getBenefitData(isBenefitActiveForPeriod));

  const getDesiredBenefits = () =>
    setDesiredBenefits(
      getBenefitData(
        (benefit) => employeeStore.currentEmployee?.benefit_settings[benefit]?.desired === true
      )
    );

  const fetchEmployeeBudgetInformation = async () => {
    if (date && employeeId) {
      await employeeStore.fetchEmployeeBudgetInformation(employeeId, format(date, 'yyyy-MM-dd'));
      setCurrentBenefitBudget(employeeStore.currentEmployee?.benefit_budget);
    }
  };

  useEffect(() => {
    let isCanceled = false;
    const fetchUser = async () => {
      if (!isCanceled && employeeId) {
        await employeeStore.fetchOne(employeeId);
        fetchEmployeeBudgetInformation();
      }
    };

    fetchUser();
    return () => {
      isCanceled = true;
    };
  }, [employeeId]);

  useEffect(() => {
    let isCanceled = false;
    const fetchBuget = async () => {
      if (!isCanceled && employeeStore.currentEmployee) {
        fetchEmployeeBudgetInformation();
      }
    };
    fetchBuget();
    return () => {
      isCanceled = true;
    };
  }, [date]);

  useEffect(() => {
    if (employeeStore.currentEmployee) {
      getActiveBenefits();
      getDesiredBenefits();
    }
  }, [currentBenefitBudget]);

  return (
    <Box className="flex flex-col gap-4 w-full">
      <ActiveBudget
        total_available_amount={currentBenefitBudget?.total_available_amount ?? 0}
        total_used_amount={currentBenefitBudget?.total_used_amount ?? 0}
        initialDate={date}
      />
      <Box className="flex flex-row justify-between w-full">
        <BenefitsWidget
          benefits={activeBenefits}
          variant="active"
          link={`/employees/${employeeId}/benefits/overview${
            date ? '?date=' + format(date, 'yyyy-MM') : ''
          }`}
        />
        <BenefitsWidget
          benefits={desiredBenefits}
          variant="desired"
          link={`/employees/${employeeId}/benefits/overview`}
        />
      </Box>
      {benefitSetsEnabled && (
        <>
          {isEmployeeLoading || isBenefitSetLoading ? (
            <Loading />
          ) : (
            <BenefitSetWidget
              benefitSet={nextMonthBenefitSet}
              employeeId={employeeData?.id}
              companyBenefitSetsExists={companyBenefitSetsExists}
            />
          )}
        </>
      )}
    </Box>
  );
});
